// Pacote 42 (PR #70) — Face ID lock no iOS via @aparajita/capacitor-biometric-auth.
//
// Estratégia:
//   - Em web: hook nunca trava (no-op). Log de "available=false" pra debug.
//   - Em iOS: se user habilitou em settings, trava o app a cada launch +
//     a cada vinda do background. Unlock requer Face ID/Touch ID.
//
// Persistência: enabled vive em localStorage (igual JWT). Não precisa
// backend porque é decisão de device — em outro device (browser web) o
// user simplesmente loga normal.
//
// Re-lock no background: App plugin do Capacitor emite appStateChange
// quando o sistema esconde o app. Setamos `unlocked=false` e o gate
// re-renderiza a tela de lock.

import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { Capacitor } from "@capacitor/core"
import { App as CapacitorApp } from "@capacitor/app"
import { BiometricAuth, BiometryError } from "@aparajita/capacitor-biometric-auth"

const STORAGE_KEY = "novavida.biometricLockEnabled"

type Platform = "ios" | "android" | "web"

interface BiometricState {
  /** Plataforma onde o app está rodando (ios | android | web). */
  platform: Platform
  /** Device suporta biometria + tem credenciais cadastradas? */
  available: boolean
  /** User habilitou o lock? Persistido em localStorage. */
  enabled: boolean
  /** App está atualmente desbloqueado nesta sessão? */
  unlocked: boolean
  /** Liga/desliga o lock. Quando liga, exige Face ID imediatamente como confirmação. */
  setEnabled: (next: boolean) => Promise<void>
  /** Dispara o prompt de biometria. Resolve true se autenticou. */
  authenticate: () => Promise<boolean>
  /** Trava manualmente — útil pra "Bloquear agora" no menu. */
  lock: () => void
}

const BiometricContext = createContext<BiometricState | null>(null)

function getStoredEnabled(): boolean {
  if (typeof localStorage === "undefined") return false
  return localStorage.getItem(STORAGE_KEY) === "1"
}

function setStoredEnabled(v: boolean) {
  if (typeof localStorage === "undefined") return
  if (v) localStorage.setItem(STORAGE_KEY, "1")
  else localStorage.removeItem(STORAGE_KEY)
}

export function BiometricLockProvider({ children }: { children: ReactNode }) {
  const platform = (Capacitor.getPlatform() as Platform) ?? "web"
  const [available, setAvailable] = useState(false)
  const [enabled, setEnabledState] = useState(getStoredEnabled())
  // Web nunca trava — começa unlocked. Native começa LOCKED se enabled.
  const [unlocked, setUnlocked] = useState(platform === "web" || !getStoredEnabled())

  // Discovery inicial: o device suporta biometria?
  useEffect(() => {
    if (platform === "web") {
      setAvailable(false)
      return
    }
    BiometricAuth.checkBiometry()
      .then((info) => setAvailable(info.isAvailable))
      .catch(() => setAvailable(false))
  }, [platform])

  // App resume: re-trava quando volta do background (iOS) — segurança extra.
  useEffect(() => {
    if (platform === "web" || !enabled) return
    let cleanup: (() => void) | undefined
    CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) {
        // Foi pra background — trava na próxima abertura
        setUnlocked(false)
      }
    }).then((handle) => {
      cleanup = () => {
        handle.remove()
      }
    })
    return () => cleanup?.()
  }, [platform, enabled])

  async function authenticate(): Promise<boolean> {
    if (platform === "web") {
      setUnlocked(true)
      return true
    }
    try {
      await BiometricAuth.authenticate({
        reason: "Desbloquear Finanças R e H",
        cancelTitle: "Cancelar",
        allowDeviceCredential: true, // permite passcode como fallback
        iosFallbackTitle: "Use senha",
        androidTitle: "Finanças R e H",
        androidSubtitle: "Confirme sua identidade",
      })
      setUnlocked(true)
      return true
    } catch (e) {
      if (e instanceof BiometryError) {
        // userCancel, biometryNotAvailable, etc — mantém locked, UI mostra retry
        return false
      }
      return false
    }
  }

  async function setEnabled(next: boolean) {
    if (next === enabled) return
    if (next) {
      // Pra LIGAR, exige autenticação ao vivo — anti-abuso (alguém pega o
      // celular do user destravado e tenta ligar pra travar quando voltar).
      const ok = await authenticate()
      if (!ok) return
    }
    setStoredEnabled(next)
    setEnabledState(next)
    if (!next) {
      // Desligou → garante que tá unlocked pra UX
      setUnlocked(true)
    }
  }

  function lock() {
    if (enabled && platform !== "web") {
      setUnlocked(false)
    }
  }

  return (
    <BiometricContext.Provider
      value={{
        platform,
        available,
        enabled,
        unlocked,
        setEnabled,
        authenticate,
        lock,
      }}
    >
      {children}
    </BiometricContext.Provider>
  )
}

export function useBiometricLock() {
  const ctx = useContext(BiometricContext)
  if (!ctx) throw new Error("useBiometricLock must be used inside BiometricLockProvider")
  return ctx
}
